import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Shield, RefreshCw, Plus } from 'lucide-react';

import {
  setPartners,
  setRoutingRules,
  setLoading,
  setError,
} from '../../reducers/responsePartnerReducer';
import {
  getResponsePartnersService,
  getRoutingRulesService,
} from '../../utils/responsePartnerService';

import ResponsePartnerList from './ResponsePartnerList';
import ResponsePartnerForm from './ResponsePartnerForm';
import RoutingRuleList from './RoutingRuleList';
import RoutingRuleForm from './RoutingRuleForm';

const TABS = [
  { id: 'partners', label: 'Response Partners' },
  { id: 'rules', label: 'Routing Rules' },
];

const ResponsePartnerDashboard = () => {
  const dispatch = useDispatch();
  const { partners, routingRules, loading, error } = useSelector(
    (state) => state.responsePartnerList || { partners: [], routingRules: [], loading: false, error: null }
  );

  const [activeTab, setActiveTab] = useState('partners');
  const [partnerFormState, setPartnerFormState] = useState({ open: false, partner: null });
  const [ruleFormState, setRuleFormState] = useState({ open: false, rule: null });

  const refreshAll = async () => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const [partnersResult, rulesResult] = await Promise.all([
        getResponsePartnersService(),
        getRoutingRulesService(),
      ]);
      dispatch(setPartners(partnersResult));
      dispatch(setRoutingRules(rulesResult));
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to load response partners';
      dispatch(setError(message));
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenCreatePartner = () => {
    setPartnerFormState({ open: true, partner: null });
  };

  const handleOpenEditPartner = (partner) => {
    setPartnerFormState({ open: true, partner });
  };

  const handleClosePartnerForm = () => {
    setPartnerFormState({ open: false, partner: null });
  };

  const handleOpenCreateRule = () => {
    setRuleFormState({ open: true, rule: null });
  };

  const handleOpenEditRule = (rule) => {
    setRuleFormState({ open: true, rule });
  };

  const handleCloseRuleForm = () => {
    setRuleFormState({ open: false, rule: null });
  };

  return (
    <div className="p-4 overflow-y-auto w-full">
      <div className="flex flex-col gap-1 mb-4">
        <div className="flex items-center gap-2 text-stone-700">
          <Shield size={22} />
          <h1 className="text-2xl font-bold m-0">Response Partners</h1>
        </div>
        <p className="text-sm text-stone-500">
          Admin workspace for managing preferred response partners and their routing rules for loss event incidents.
        </p>
      </div>

      <div className="flex flex-row items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-amp-track-section="Response Partner Dashboard"
              data-amp-track-action="Tab_Click"
              data-amp-track-description={`Switch to ${tab.label}`}
              className={`flex items-center text-md font-semibold whitespace-nowrap gap-2 px-3 py-2 border-solid border rounded-3xl border-stone-300
                ${activeTab === tab.id
                  ? 'text-white bg-stone-600 hover:bg-stone-500'
                  : 'text-stone-600 bg-transparent hover:bg-sky-500 hover:text-white'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <button
            onClick={refreshAll}
            disabled={loading}
            data-amp-track-section="Response Partner Dashboard"
            data-amp-track-action="Refresh_Click"
            data-amp-track-description="Refresh Response Partners"
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-transparent text-stone-700 hover:bg-stone-200 disabled:opacity-50"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          {activeTab === 'partners' ? (
            <button
              onClick={handleOpenCreatePartner}
              data-amp-track-section="Response Partner Dashboard"
              data-amp-track-action="Add_Partner_Click"
              data-amp-track-description="Open Create Partner Form"
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-500"
            >
              <Plus size={18} /> Add Partner
            </button>
          ) : (
            <button
              onClick={handleOpenCreateRule}
              disabled={partners.length === 0}
              data-amp-track-section="Response Partner Dashboard"
              data-amp-track-action="Add_Rule_Click"
              data-amp-track-description="Open Create Rule Form"
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-50"
              title={partners.length === 0 ? 'Create a partner first to add rules' : ''}
            >
              <Plus size={18} /> Add Rule
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {activeTab === 'partners' && (
        <ResponsePartnerList
          partners={partners}
          loading={loading}
          onEdit={handleOpenEditPartner}
        />
      )}

      {activeTab === 'rules' && (
        <RoutingRuleList
          rules={routingRules}
          partners={partners}
          loading={loading}
          onEdit={handleOpenEditRule}
        />
      )}

      {partnerFormState.open && (
        <ResponsePartnerForm
          partner={partnerFormState.partner}
          onClose={handleClosePartnerForm}
        />
      )}

      {ruleFormState.open && (
        <RoutingRuleForm
          rule={ruleFormState.rule}
          partners={partners}
          rules={routingRules}
          onClose={handleCloseRuleForm}
        />
      )}
    </div>
  );
};

export default ResponsePartnerDashboard;
